import { __awaiter } from "tslib";
import jwt_decode from "jwt-decode";
import superagent from "superagent";
import { getErrorMessage } from "../../utilities/super-agent-helpers";
import { convertURLToOAuth2, getBearerToken } from "../../utilities/auth-utils";
import { urlParams as pageUrlParams } from "../../utilities/url-params";
import initials from "initials";
import { maybeAddResearcherParam } from "../../utilities/researcher-param";
export const parseUrl = (url) => {
    const parser = document.createElement("a");
    parser.href = url;
    return parser;
};
export const PORTAL_JWT_URL_SUFFIX = "api/v1/jwt/portal";
export class Portal {
    constructor(urlParams) {
        Object.defineProperty(this, "rawPortalJWT", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "portalJWT", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "urlParams", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "bearerToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "basePortalUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "portalHost", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "classInfoUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "offeringId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isPortalPreview", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        this.urlParams = urlParams || pageUrlParams;
        this.bearerToken = getBearerToken(this.urlParams);
        this.isPortalPreview = !!this.urlParams.domain && !!this.urlParams.domain_uid && !this.bearerToken;
    }
    ensureTrailingSlash(url) {
        if (url.endsWith("/")) {
            return url;
        }
        return `${url}/`;
    }
    getBasePortalUrl() {
        const { urlParams } = this;
        if (urlParams.reportType) {
            if (urlParams.reportType !== "offering") {
                throw "Sorry, only external reports at the offering level are supported";
            }
            if (!urlParams.class) {
                throw "Missing class parameter!";
            }
            if (!urlParams.offering) {
                throw "Missing offering parameter!";
            }
            const { protocol, host } = parseUrl(urlParams.class);
            return `${protocol}//${host}/`;
        }
        else if (urlParams.domain) {
            return this.ensureTrailingSlash(urlParams.domain);
        }
        else if (urlParams.authDomain) {
            return this.ensureTrailingSlash(urlParams.authDomain);
        }
        else {
            throw "Missing domain query parameter!";
        }
    }
    requestPortalJWT({ standaloneAuthUser, basePortalUrl, bearerToken } = {}) {
        return new Promise((resolve, reject) => {
            var _a;
            const done = (rawJWT) => {
                const portalJWT = jwt_decode(rawJWT);
                if (portalJWT) {
                    this.portalJWT = portalJWT;
                    this.rawPortalJWT = rawJWT;
                    resolve({ rawPortalJWT: rawJWT, portalJWT: this.portalJWT });
                }
                else {
                    reject("Invalid portal token");
                }
            };
            if (standaloneAuthUser) {
                done(standaloneAuthUser.rawJWT);
                return;
            }
            const params = new URLSearchParams();
            if (pageUrlParams.resourceLinkId) {
                params.append("resource_link_id", pageUrlParams.resourceLinkId);
            }
            if (pageUrlParams.targetUserId) {
                params.append("target_user_id", pageUrlParams.targetUserId);
            }
            const queryString = params.size > 0 ? `?${params.toString()}` : "";
            // eslint-disable-next-line max-len
            const url = `${this.ensureTrailingSlash((_a = basePortalUrl !== null && basePortalUrl !== void 0 ? basePortalUrl : this.basePortalUrl) !== null && _a !== void 0 ? _a : "")}${PORTAL_JWT_URL_SUFFIX}${queryString}`;
            superagent
                .get(maybeAddResearcherParam(url))
                .set("Authorization", `Bearer ${bearerToken !== null && bearerToken !== void 0 ? bearerToken : this.bearerToken}`)
                .end((err, res) => {
                if (err) {
                    reject(getErrorMessage(err, res));
                }
                else if (!res.body || !res.body.token) {
                    reject("No token found in JWT request response");
                }
                else {
                    done(res.body.token);
                }
            });
        });
    }
    initialize(standaloneAuthUser) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.bearerToken) {
                throw "No token provided for authentication (must launch from Portal)";
            }
            if (standaloneAuthUser === null || standaloneAuthUser === void 0 ? void 0 : standaloneAuthUser.jwt) {
                this.basePortalUrl = standaloneAuthUser === null || standaloneAuthUser === void 0 ? void 0 : standaloneAuthUser.jwt.domain;
            }
            else {
                this.basePortalUrl = this.getBasePortalUrl();
            }
            yield this.requestPortalJWT({ standaloneAuthUser });
            const { basePortalUrl, portalJWT, urlParams } = this;
            const supportedUserTypes = ["learner", "teacher", "researcher"];
            if (!supportedUserTypes.includes(portalJWT.user_type)) {
                throw new Error(`Only ${supportedUserTypes.join(" or ")} logins are currently supported! ` +
                    `Unsupported type: ${(_a = portalJWT.user_type) !== null && _a !== void 0 ? _a : "(unknown user type)"}`);
            }
            this.portalHost = parseUrl(basePortalUrl).host;
            if (portalJWT.user_type === "learner") {
                this.classInfoUrl = portalJWT.class_info_url;
                this.offeringId = `${portalJWT.offering_id}`;
            }
            else if (urlParams && urlParams.class && urlParams.offering) {
                this.classInfoUrl = urlParams.class;
                this.offeringId = urlParams.offering.split("/").pop();
            }
            else if (standaloneAuthUser) {
                this.classInfoUrl = `${basePortalUrl}api/v1/classes/${standaloneAuthUser.classId}`;
                this.offeringId = String(standaloneAuthUser.offeringId);
            }
            if (!this.classInfoUrl || !this.offeringId) {
                throw new Error("Unable to get classInfoUrl or offeringId");
            }
            // Re-Write the URL with OAuth2 parameters
            const oAuth2Url = convertURLToOAuth2(window.location.href, basePortalUrl, this.offeringId);
            if (oAuth2Url) {
                window.history.replaceState(null, "CLUE", oAuth2Url.toString());
            }
        });
    }
    /**
     * This must be called after initialize()
     *
     * @returns the current class info from the portal
     */
    getClassInfo() {
        const { classInfoUrl, rawPortalJWT, portalHost: portal, offeringId } = this;
        return new Promise((resolve, reject) => {
            superagent
                .get(maybeAddResearcherParam(classInfoUrl))
                .set("Authorization", `Bearer/JWT ${rawPortalJWT}`)
                .end((err, res) => {
                if (err) {
                    reject(getErrorMessage(err, res));
                }
                else if (!res.body || !res.body.class_hash) {
                    reject("Invalid class info response");
                }
                else {
                    const rawClassInfo = res.body;
                    const classInfo = {
                        localTimestamp: Date.now(),
                        name: rawClassInfo.name,
                        classHash: rawClassInfo.class_hash,
                        students: rawClassInfo.students.map((rawStudent) => {
                            const fullName = `${rawStudent.first_name} ${rawStudent.last_name}`;
                            const id = rawStudent.id.split("/").pop() || "0";
                            const student = {
                                type: "student",
                                id,
                                portal,
                                firstName: rawStudent.first_name,
                                lastName: rawStudent.last_name,
                                fullName,
                                className: rawClassInfo.name,
                                initials: initials(fullName),
                                classHash: rawClassInfo.class_hash,
                                offeringId,
                            };
                            return student;
                        }),
                        teachers: rawClassInfo.teachers.map((rawTeacher) => {
                            const fullName = `${rawTeacher.first_name} ${rawTeacher.last_name}`;
                            const id = rawTeacher.id.split("/").pop() || "0";
                            const teacher = {
                                type: "teacher",
                                id,
                                portal,
                                firstName: rawTeacher.first_name,
                                lastName: rawTeacher.last_name,
                                fullName,
                                className: rawClassInfo.name,
                                initials: initials(fullName),
                                classHash: rawClassInfo.class_hash,
                                offeringId,
                            };
                            return teacher;
                        }),
                    };
                    resolve(classInfo);
                }
            });
        });
    }
}
//# sourceMappingURL=data:application/json;base64,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